/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Render from '../render/Render';
import {dlsColor} from '../theme/theme';

export interface FilterEmptyStateAction {
  label: string;
  onClick: () => void;
}

export interface FilterEmptyStateProps {
  actionLabel?: string;
  actions?: FilterEmptyStateAction[];
  className?: string;
  description?: string;
  icon?: ReactNode;
  onAction?: () => void;
  title: string;
}

const useStyles = makeStyles(() => ({
  root : {
    alignItems     : 'flex-end',
    alignSelf      : 'stretch',
    boxSizing      : 'border-box',
    display        : 'flex',
    flexDirection  : 'column',
    gap            : 4,
    height         : 216,
    justifyContent : 'center',
    padding        : '24px 16px',
    width          : '100%'
  },
  panel : {
    alignItems      : 'center',
    backgroundColor : dlsColor('grey13'),
    borderRadius    : 12,
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 8,
    height          : 168,
    justifyContent  : 'center',
    padding         : 16,
    width           : '100%'
  },
  icon : {
    alignItems     : 'center',
    display        : 'flex',
    justifyContent : 'center'
  },
  text : {
    alignItems     : 'center',
    alignSelf      : 'stretch',
    color          : dlsColor('grey2'),
    display        : 'flex',
    flexDirection  : 'column',
    fontFamily     : 'Inter',
    fontSize       : 20,
    fontStyle      : 'normal',
    height         : 88,
    justifyContent : 'center',
    lineHeight     : '22px',
    textAlign      : 'center',
    width          : '100%',
    gap            : 16
  },
  title : {
    fontWeight : 700,
    maxWidth   : '100%',
    overflow   : 'hidden',
    whiteSpace : 'nowrap'
  },
  description : {
    fontWeight : 400,
    maxWidth   : '100%',
    overflow   : 'hidden',
    fontSize   : 16
  },
  action : {
    alignItems      : 'center',
    backgroundColor : 'transparent',
    border          : 0,
    boxSizing       : 'border-box',
    cursor          : 'pointer',
    display         : 'flex',
    flexDirection   : 'row',
    flexGrow        : 0,
    flexShrink      : 0,
    height          : 32,
    justifyContent  : 'center',
    padding         : '4px 16px'
  },
  actionLabel : {
    alignItems    : 'center',
    color         : dlsColor('darkestBlue'),
    display       : 'flex',
    flexGrow      : 0,
    flexShrink    : 0,
    fontFamily    : 'Inter',
    fontSize      : 13,
    fontStyle     : 'normal',
    fontWeight    : 600,
    height        : 16,
    letterSpacing : 0.5,
    lineHeight    : '16px'
  },
  actions : {
    alignItems     : 'center',
    display        : 'flex',
    flexDirection  : 'row',
    gap            : 8,
    justifyContent : 'center'
  }
}));

/**
 * Displays an empty state inside a filter drawer category.
 *
 * ```typescript
 * import FilterEmptyState from '@ion/dls/src/filters/FilterEmptyState';
 *
 * ...
 *
 * <FilterEmptyState title='No Routes to Filter Yet'
 *   description='Create Routes for this group to enable filtering.' />
 * ```
 */
export const FilterEmptyState = ({
  actionLabel,
  actions,
  className,
  description,
  icon,
  onAction,
  title
}: FilterEmptyStateProps) => {
  const classes = useStyles();

  const emptyStateActions = actions ?? (actionLabel && onAction ? [{
    label   : actionLabel,
    onClick : onAction
  }] : []);

  return (
    <div className={className ? `${classes.root} ${className}` : classes.root}>
      <div className={classes.panel}>
        <Render.If condition={!!icon}>
          <div className={classes.icon}>{icon}</div>
        </Render.If>
        <div className={classes.text}>
          <div className={classes.title}>{title}</div>
          <Render.If condition={!!description}>
            <div className={classes.description}>{description}</div>
          </Render.If>
        </div>
        <Render.If condition={!!emptyStateActions.length}>
          <div className={classes.actions}>
            {emptyStateActions.map(action => (
              <button key={action.label}
                type='button'
                className={classes.action}
                onClick={action.onClick}>
                <span className={classes.actionLabel}>{action.label}</span>
              </button>
            ))}
          </div>
        </Render.If>
      </div>
    </div>
  );
};

export default memo(FilterEmptyState);
